/**
 * Copyright (c) 2024 Securisea, Inc.
 * All rights reserved.
 *
 * This is unpublished, propriety, internal
 * source code of Securisea, Inc.
 * No license is provided to any third parties.
 * If found outside of a Securisea, Inc. environment
 * report to bounty@securisea.com for a possible cash reward
 * and promptly delete all files.
 */
import React, { useState, useEffect, FunctionComponent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import { useGetControl } from "../../../hooks/controls";

import CloseIcon from "@mui/icons-material/Close";
import Layout from "../../../layout";
import { Control } from "../../../types/controls";
import { TabPanel, a11yProps } from "../../../helpers/functions";
import PredecessorControlTable from "../../tables/predecessor_controls";
import ControlVideosComponent from "./videos";
import { defaultControl } from "./admin";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import { CancelOutlined } from "@mui/icons-material";

interface EditControlModalProps {
  control?: Partial<Control> | null;
  isOpen?: boolean;
  mode?: "modal" | "page";
  onClose?: () => void;
}

const UserViewControlModal: FunctionComponent<EditControlModalProps> = ({
  control = defaultControl,
  isOpen = true,
  mode = "page",
  onClose = () => {},
}) => {
  const [editedControl, setEditedControl] = useState<
    Control | Partial<Control>
  >(control || defaultControl);
  const isPageMode = mode === "page";

  const { id } = useParams<{ id: string }>();
  const [loadingError, setLoadingError] = useState(false);

  if (id) {
    const { data, isLoading: isFetching } = useGetControl({ id });
    useEffect(() => {
      if (mode === "page" && id && data && !isFetching) {
        if (data[0].controls.length === 0) {
          setLoadingError(true);
        } else {
          const deepCopiedControl = JSON.parse(
            JSON.stringify(data[0].controls[0])
          );
          setEditedControl(deepCopiedControl);
        }
      }
    }, [id, mode, data, isFetching]);
  }

  const [activeTab, setActiveTab] = useState(0);

  const handleTabClick = (tabId: number) => {
    setActiveTab(tabId);
  };

  const pageModeContent = (
    <>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => handleTabClick(newValue)}
            aria-label="Switch tabs"
          >
            <Tab label="Predecessor Controls" {...a11yProps(0)} />
            <Tab label="Control Videos" {...a11yProps(1)} />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          {editedControl.id && (
            <PredecessorControlTable
              parentObjectUrlKey="controls"
              loadPartial={true}
              parentObject={editedControl}
              relatedChildFieldName="predecessor_controls"
              showRowCheckboxes={false}
            />
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {editedControl.id && (
            <>
              <Grid container gap={2} sx={{ m: 2 }}>
                <ControlVideosComponent
                  video_type="control_description"
                  control={editedControl}
                />
                {/* <ControlVideosComponent
                    video_type="evidence_description"
                    control={editedControl}
                  /> */}
              </Grid>
            </>
          )}
        </TabPanel>
      </Box>
    </>
  );

  const content = (
    <DialogContent>
      <Card elevation={2}>
        <CardHeader
          title={editedControl.name}
          subheader={editedControl.title}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        />
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Description
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
            }}
          >
            <ReactMarkdown>{editedControl.description}</ReactMarkdown>
          </Paper>

          <Typography variant="h6" gutterBottom>
            Control Types
          </Typography>
          <List>
            {[
              { label: "Preventive", value: editedControl.preventive },
              { label: "Administrative", value: editedControl.administrative },
              { label: "Detective", value: editedControl.detective },
            ].map((item) => (
              <ListItem key={item.label}>
                <ListItemIcon>
                  {item.value ? (
                    <GridCheckCircleIcon color="success" />
                  ) : (
                    <CancelOutlined color="error" />
                  )}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </DialogContent>
  );

  if (loadingError) {
    return (
      <Layout pageTitle="" parentUrl="" parentPageName="">
        <Typography variant="h5" sx={{ color: "black", padding: 3 }}>
          Control not found
        </Typography>
        ;
      </Layout>
    );
  } else
    return isPageMode ? (
      <Layout pageTitle="Control" parentUrl="/" parentPageName="Controls">
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                color: "black",
                borderColor: "divider",
              }}
            >
              {content}
            </Box>
            <Box
              sx={{
                width: "98%",
                maxWidth: "98%",
                color: "black",
                border: 2,
                borderColor: "divider",
                margin: 2,
                borderRadius: 3,
              }}
            >
              {pageModeContent}
            </Box>
          </Box>
        </Box>
      </Layout>
    ) : (
      <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography fontSize={25}>{editedControl.name}</Typography>
          <CloseIcon
            onClick={onClose}
            style={{
              position: "absolute",
              right: 10,
              top: 10,
              fontSize: 30,
              cursor: "pointer",
            }}
          />
        </DialogTitle>

        {content}
      </Dialog>
    );
};

export default UserViewControlModal;
